import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '../ui/input';
import { Send, Search, User, Loader, Building2, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = API?.replace('https://', 'wss://').replace('http://', 'ws://');

export default function MessagingSystem({ userType = 'student' }) {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null); // { other_user_id, other_user }
  const [newMsg, setNewMsg] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [propDetails, setPropDetails] = useState(null); // property card for current conv
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Load conversations
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/messages/conversations`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setConversations(Array.isArray(data) ? data : []))
      .catch(() => setConversations([]));
  }, [token]);

  // Load messages + open WebSocket when conversation selected
  const openConversation = useCallback(async (conv) => {
    setSelected(conv);
    setPropDetails(null);
    setLoading(true);
    wsRef.current?.close();

    const otherId = conv.other_user_id;
    const msgs = await fetch(`${API}/api/messages/conversation/${otherId}`, { headers })
      .then(r => r.ok ? r.json() : [])
      .catch(() => []);
    setMessages(msgs);
    setLoading(false);

    // Fetch property details if this conversation is about a specific property
    if (conv.property_id) {
      fetch(`${API}/api/properties/${conv.property_id}`)
        .then(r => r.ok ? r.json() : null)
        .then(p => { if (p) setPropDetails(p); })
        .catch(() => {});
    }

    // Open WebSocket for real-time
    const ws = new WebSocket(`${WS_BASE}/api/messages/ws/${otherId}?token=${token}`);
    ws.onmessage = (e) => {
      const msg = JSON.parse(e.data);
      setMessages(prev => {
        if (prev.find(m => m.message_id === msg.message_id)) return prev;
        return [...prev, msg];
      });
    };
    wsRef.current = ws;
  }, [token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => wsRef.current?.close();
  }, []);

  const sendMessage = async () => {
    if (!newMsg.trim() || !selected) return;
    const content = newMsg.trim();
    setNewMsg('');

    const res = await fetch(`${API}/api/messages/send`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipient_id: selected.other_user_id, content }),
    });
    if (res.ok) {
      const msg = await res.json();
      setMessages(prev => [...prev, msg]);
      // Update conversations list
      setConversations(prev => prev.map(c =>
        c.other_user_id === selected.other_user_id
          ? { ...c, last_message: content, last_message_at: new Date().toISOString() }
          : c
      ));
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  const filtered = conversations.filter(c =>
    !search || c.other_user?.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-[600px] border border-sky-100 rounded-2xl overflow-hidden bg-white shadow-sm">
      {/* Conversations sidebar */}
      <div className="w-72 border-r border-sky-100 flex flex-col">
        <div className="p-3 border-b border-sky-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search conversations…"
              className="pl-9 text-sm border-sky-200" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm px-4">
              <User className="w-10 h-10 mx-auto mb-2 text-gray-200" />
              No conversations yet. Start one from a property page.
            </div>
          ) : filtered.map((conv, i) => (
            <button key={i} onClick={() => openConversation({ ...conv, other_user_id: conv.other_user?.id || conv.conversation_id?.split(':').find(p => p !== user?.id) })}
              className={`w-full text-left px-4 py-3 hover:bg-sky-50 transition-colors border-b border-gray-50 ${selected?.conversation_id === conv.conversation_id ? 'bg-sky-50' : ''}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#094f6f]/10 rounded-full flex items-center justify-center text-[#094f6f] font-bold flex-shrink-0">
                  {conv.other_user?.full_name?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{conv.other_user?.full_name || 'User'}</p>
                  <p className="text-xs text-gray-400 truncate">{conv.last_message || 'No messages yet'}</p>
                </div>
                {conv.unread_count > 0 && (
                  <span className="w-5 h-5 bg-[#094f6f] text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                    {conv.unread_count}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 flex flex-col">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <Send className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-sm">Select a conversation to start messaging</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="px-5 py-3 border-b border-sky-100 bg-sky-50/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#094f6f]/10 rounded-full flex items-center justify-center text-[#094f6f] font-bold text-sm">
                  {selected.other_user?.full_name?.[0] || '?'}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{selected.other_user?.full_name || 'User'}</p>
                  <p className="text-[10px] text-gray-400 capitalize">{selected.other_user?.role || (userType === 'student' ? 'Landlord' : 'Student')}</p>
                </div>
              </div>

              {/* Property reference card */}
              {propDetails && (
                <div className="mt-2 flex items-center gap-2 bg-white rounded-xl p-2 border border-sky-100 shadow-sm">
                  {propDetails.images?.[0] ? (
                    <img src={propDetails.images[0]} alt={propDetails.name}
                      className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-[#094f6f]/40" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{propDetails.name}</p>
                    <p className="text-[10px] text-gray-400">{propDetails.city} · A${propDetails.price_per_week}/wk</p>
                  </div>
                  <Link to={`/property/${propDetails.id}`} target="_blank"
                    className="text-[#094f6f] hover:text-[#094f6f]/70 flex-shrink-0"
                    title="View property">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading && <div className="flex justify-center"><Loader className="w-5 h-5 text-[#094f6f] animate-spin" /></div>}
              {messages.map((msg, i) => {
                const isMine = msg.sender_id === user?.id;
                return (
                  <div key={i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${isMine ? 'bg-[#094f6f] text-white rounded-br-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`}>
                      <p className="leading-relaxed">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-gray-400'}`}>{formatTime(msg.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-sky-100 flex gap-2">
              <Input value={newMsg} onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder="Type a message…"
                className="flex-1 text-sm border-sky-200"
                data-testid="message-input" />
              <button onClick={sendMessage} disabled={!newMsg.trim()}
                className="w-9 h-9 bg-[#094f6f] text-white rounded-xl flex items-center justify-center disabled:opacity-40 hover:bg-[#094f6f]/90 transition-colors"
                data-testid="message-send">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
